/**
 * Progreso de pasos de una consulta. **Componente propio.**
 *
 * Adaptado de `EstadoNotificacionBadge` de Recuperos, con una diferencia que
 * es la razón de reescribirlo: allí el total es la constante 9, porque hay un
 * solo circuito. Aquí el total **llega por prop** desde la definición del
 * canal, porque el físico tiene 9 pasos, correo y mesa de partes virtual 3, y
 * consulta web 2.
 *
 * `UI-3`: nunca solo color — el número va escrito y hay texto para lectores de
 * pantalla. `UI-4`: el tono sale del estado que decide el backend, y nunca es
 * el azul de marca.
 */
import type { Tono } from '../api/tipos.js'
import { ConAyuda } from './ConAyuda.js'

interface PropsBadgePaso {
  paso: number
  totalPasos: number
  tono: Tono
  /** Descripción del paso actual, para el título accesible. */
  descripcion?: string
}

export function BadgePaso({ paso, totalPasos, tono, descripcion }: PropsBadgePaso) {
  const puntos = Array.from({ length: totalPasos }, (_, indice) => indice + 1)
  const texto = `Paso ${String(paso)} de ${String(totalPasos)}${descripcion ? `: ${descripcion}` : ''}`

  const badge = (
    // `tabIndex` deliberado, contra la recomendación por defecto del linter:
    // es el patrón WAI-ARIA para un disparador de ayuda que no es un control.
    // Quitarlo dejaría el detalle del circuito fuera del alcance del teclado,
    // que es justo lo que este componente vino a arreglar. Son pocos por
    // pantalla, así que no infla el recorrido de tabulación.
    // `mpfn-control` le da el contrato de foco del design system; el `outline`
    // no se escribe a mano (`UI-5`).
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <span className="mpfn-badge-paso mpfn-control" data-tono={tono} tabIndex={0}>
      <span className="mpfn-badge-paso__puntos" aria-hidden="true">
        {puntos.map((numero) => (
          <span
            key={numero}
            className="mpfn-badge-paso__punto"
            data-cumplido={numero <= paso ? 'si' : 'no'}
          />
        ))}
      </span>
      <span className="mpfn-badge-paso__cifra" aria-hidden="true">
        {paso}/{totalPasos}
      </span>
      <span className="mpfn-sr-only">{texto}</span>
    </span>
  )

  // Sin descripción no hay nada que mostrar al posarse, y un disparador de
  // ayuda vacío solo añade un elemento tabulable de más.
  if (descripcion === undefined) return badge

  return (
    <ConAyuda
      ayuda={
        <>
          <strong>
            Paso {paso} de {totalPasos}
          </strong>
          <span className="sigfisc-ayuda__estado">{descripcion}</span>
        </>
      }
    >
      {badge}
    </ConAyuda>
  )
}
